import { useEffect, useState } from 'react'
import type { GameView } from '@/shared/game/types'
import { ChatBox } from './ChatBox'
import { showsHistory } from './rules'
import type { ChatMessage } from './types'

type Props = {
  view: GameView
  messages: ChatMessage[]
}

/**
 * Чат в партии — шторкой поверх доски.
 *
 * Блоком в потоке экрана он быть не может: блок «руки» под доской держит
 * фиксированную высоту нарочно, доска центрируется по свободной высоте, и
 * стоит чему-то под ней появиться или исчезнуть — доска съезжает. Про это
 * предупреждают и GameScreen, и docs/design/README.md.
 *
 * Отсюда и кнопка со счётчиком непрочитанных: шторка закрыта большую часть
 * партии, и разговор должен как-то позвать. Счётчик считается от того, что
 * было видно при последнем открытии, — своих реплик в нём нет, они и так
 * прочитаны.
 *
 * За открытым столом счётчика нет вовсе: истории там не показывается
 * (showsHistory), и звать в шторку не за чем — открывший её увидит только
 * набор фраз. Сказанное там видно плашкой у двора говорившего.
 */
export function ChatDock({ view, messages }: Props) {
  const [open, setOpen] = useState(false)
  const [seen, setSeen] = useState(0)

  const history = showsHistory(view)
  const last = messages[messages.length - 1]?.seq ?? 0
  const unread = !history
    ? 0
    : messages.filter((message) => message.seq > seen && message.seat !== view.you?.seat).length

  // Открытая шторка читается сама: пока она видна, непрочитанных нет.
  useEffect(() => {
    if (open) setSeen(last)
  }, [open, last])

  /*
   * Открытая шторка убирает круглую кнопку и показывает крестик у себя
   * в углу — а не держит кнопку поверх собственного содержимого. Держала:
   * кнопка стояла в том же углу, и под неё приходилось отводить полосу
   * пустоты в четыре строки, за которой всё равно пряталась последняя
   * строка списка.
   */
  return open ? (
    <div className="chat-drawer">
      <div className="chat-drawer__head">
        <b className="chat-drawer__title">За столом</b>
        <button
          className="chat-drawer__close"
          type="button"
          aria-label="закрыть чат"
          onClick={() => setOpen(false)}
        >
          <span aria-hidden="true">×</span>
        </button>
      </div>

      <ChatBox view={view} messages={messages} />
    </div>
  ) : (
    <button
      className="chat-toggle"
      type="button"
      aria-label="открыть чат"
      onClick={() => setOpen(true)}
    >
      <span aria-hidden="true">💬</span>
      {unread > 0 && <span className="chat-toggle__count">{unread}</span>}
    </button>
  )
}
